import { SHA256 } from 'crypto-js';
import { camelCase, mapKeys } from 'lodash';

export const camelizeKeys = (raw: Record<string, unknown>) => mapKeys(raw, (_, k: string) => camelCase(k));

export const capitalize = (raw: string) => `${raw.charAt(0).toUpperCase()}${raw.slice(1)}`;

/**
 * Converts a user email (or avatar field) to a Gravatar URL using SHA256 hash
 * @param email - The user's email address or avatar field value
 * @param size - Optional size parameter for the avatar (default: 200)
 * @returns Gravatar URL with SHA256 hash
 */
export const getGravatarUrl = (email: string, size: number = 200): string => {
  // Normalize email: trim whitespace and convert to lowercase
  const normalizedEmail = email.trim().toLowerCase();

  // Generate SHA256 hash using crypto-js
  // oxlint-disable-next-line new-cap -- crypto-js's SHA256 is a plain function, not a constructor; this is its documented call form
  const hash = SHA256(normalizedEmail).toString();

  // Return Gravatar URL
  return `https://gravatar.com/avatar/${hash}.jpg?s=${size}&d=mp&r=g`;
};

/**
 * Type guard to check for a nested response.status on an error-like object
 */
function hasResponseStatus(e: unknown): e is { response: { status?: number } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response?: unknown }).response === 'object' &&
    (e as { response?: { status?: unknown } }).response !== null &&
    'status' in (e as { response: { status?: unknown } }).response
  );
}

/**
 * Type guard to check for a top-level status on an error-like object
 */
function hasStatus(e: unknown): e is { status?: number } {
  return typeof e === 'object' && e !== null && 'status' in (e as { status?: unknown });
}

/**
 * Safely extract an HTTP status code from a variety of error shapes.
 * Supports either top-level `status` or nested `response.status`.
 */
export function getStatusFromError(error: unknown): number | undefined {
  if (hasResponseStatus(error)) {
    const value = error.response.status;
    return typeof value === 'number' ? value : undefined;
  }
  if (hasStatus(error)) {
    const value = error.status;
    return typeof value === 'number' ? value : undefined;
  }
  return undefined;
}

/**
 * Checks if the current environment is a production environment.
 * Production environments include: production, federal_production, and eu_production.
 */
export const isProduction = (): boolean => {
  const mode = process.env.VITALSTATISTIX_MODE;
  return mode === 'production' || mode === 'federal_production' || mode === 'eu_production';
};
